import { Dispatch, HTMLAttributes, SetStateAction, useCallback, useMemo, useState } from 'react';
import { usePlatformSelect, useStoreInfoSelect } from '../ImplementData/components/Filter/Provider';
import { Radio, Select, Tag, Tooltip } from 'antd';
import { QuickRangePicker } from 'components/QuickRangePicker';
import { useTranslate } from '@tolgee/react';
import { last30days, last7days } from '../ImplementData/utils';
import dayjs, { Dayjs } from 'dayjs';
import { css } from '@emotion/css';
import styled from '@emotion/styled';
import ItemSelect from './ItemSelect';
import { What } from 'views/Aide/Scenario/PolicyTable';

const supportPlatforms: General.PlatformUpperCase[] = [
  'SHOPEE',
  'LAZADA',
  'MERCADO',
  'TIKTOK',
  'TIKTOK_US',
];

function Filter({
  isVoice,
  departments,
  value,
  onChange,
  ...otherProps
}: {
  isVoice?: boolean;
  departments: Dashboard.Department[];
  value: ReviewAnalysis.OverviewFilter;
  onChange: Dispatch<SetStateAction<ReviewAnalysis.OverviewFilter>>;
} & Omit<HTMLAttributes<HTMLDivElement>, 'onChange'>) {
  const { platform, storeInfo, stars, startTime, endTime, itemRange } = value;

  const { t } = useTranslate();

  const [itemSelectKey, setItemSelectKey] = useState(0);

  const updateValue = useCallback(
    (partial: Partial<ReviewAnalysis.OverviewFilter>, updateItemSelectKey = true) => {
      onChange((pre) => ({
        ...pre,
        ...partial,
      }));
      if (updateItemSelectKey) {
        setItemSelectKey((pre) => pre + 1);
      }
    },
    [onChange],
  );

  const { regionInfo, platformOptions, platformSelect } = usePlatformSelect({
    scene: 'ORDER_STAT',
    showPlatformAll: true,
    departments,
    value: platform || 'ALL',
    platforms: supportPlatforms,
    onChange: (value) => {
      updateValue({
        platform: value === 'ALL' ? undefined : value,
        storeInfo: {},
        spuKeys: [],
      });
    },
  });

  const { storeInfoSelect } = useStoreInfoSelect({
    departments,
    regionInfo,
    platform,
    platformOptions,
    storeInfo,
    onChange: (storeInfo) =>
      updateValue({
        storeInfo,
        spuKeys: [],
      }),
    scene: 'ORDER_STAT',
  });

  const starsSelect = useMemo(() => {
    return (
      <Select
        allowClear
        mode="multiple"
        placeholder={t('选择星级')}
        style={{ width: 120 }}
        value={stars}
        onChange={(stars) => updateValue({ stars, spuKeys: [] })}
        options={new Array(isVoice ? 5 : 3)
          .fill('')
          .map((_, index) => ({ value: index + 1, label: t('review.x星', { count: index + 1 }) }))}
        tagRender={({ value, label, onClose }) => {
          if (stars.length === 1) {
            return (
              <Tag closable onClose={onClose}>
                {label}
              </Tag>
            );
          }
          if (value === stars[0]) {
            return (
              <Tag closable onClose={() => updateValue({ stars: [], spuKeys: [] })}>
                {t('aide.已选x项', { x: stars.length })}
              </Tag>
            );
          }
          return <></>;
        }}
      />
    );
  }, [updateValue, stars, t, isVoice]);

  const itemSelect = (
    <ItemSelect
      key={itemSelectKey}
      filter={value}
      value={value.spuKeys}
      onChange={(spuKeys) => updateValue({ spuKeys }, false)}
    />
  );

  const [dates, setDates] = useState<[Dayjs | undefined, Dayjs | undefined] | undefined>();

  const rangeSelect = useMemo(() => {
    return (
      <QuickRangePicker
        quickActions={
          isVoice
            ? []
            : [
                {
                  key: 'last7days',
                  label: t('remind.近x天', { x: 7 }),
                  range: [last7days()[0].startOf('days'), last7days()[1].endOf('days')],
                },
                {
                  key: 'last30days',
                  label: t('remind.近x天', { x: 30 }),
                  range: [last30days()[0].startOf('days'), last30days()[1].endOf('days')],
                },
              ]
        }
        value={[dayjs(Number(startTime)), dayjs(Number(endTime))]}
        disabledDate={(date) => {
          if (
            date.isSameOrAfter(dayjs(), 'days') ||
            date.isBefore(dayjs().subtract(180, 'days').startOf('days'), 'days')
          ) {
            return true;
          }
          if (dates) {
            const [start, end] = dates;
            const tooLate = start && date.diff(start, 'days') > 180;
            const tooEarly = end && end.diff(date, 'days') > 180;
            return Boolean(tooEarly) || Boolean(tooLate);
          }
          return false;
        }}
        dropdownClassName={cls}
        onCalendarChange={setDates as any}
        onChange={(value) => {
          const [start, end] = value || [];
          if (start) {
            updateValue({
              startTime: start.startOf('days').valueOf().toString(),
              endTime: (end ?? start).endOf('days').valueOf().toString(),
              spuKeys: [],
            });
          }
        }}
        onOpenChange={(open) => {
          if (open) {
            setDates(undefined);
          }
        }}
      />
    );
  }, [dates, endTime, updateValue, startTime, t, isVoice]);

  const itemRangeRadio = isVoice && (
    <section>
      <Radio.Group
        value={itemRange}
        onChange={({ target: { value } }) => updateValue({ itemRange: value, spuKeys: [] })}
      >
        <Radio.Button value="ALL">{t('aide.全部商品')}</Radio.Button>
        <Radio.Button value="RECENTLY_CREATE">
          {t('近期上架')}
          <Tooltip title={t('近30天上架的商品')}>
            <What />
          </Tooltip>
        </Radio.Button>
        <Radio.Button value="TOP_SALE">
          {t('近期高销量')}
          <Tooltip title={t('从上月到今日销量排行在前100且销量不小于10的商品')}>
            <What />
          </Tooltip>
        </Radio.Button>
      </Radio.Group>
    </section>
  );

  return (
    <Container {...otherProps}>
      {itemRangeRadio}
      <div>
        <section>
          {platformSelect}
          {storeInfoSelect}
          {starsSelect}
          {itemSelect}
        </section>
        {rangeSelect}
      </div>
    </Container>
  );
}

const cls = css`
  .ant-picker-cell-disabled {
    pointer-events: auto;
    cursor: not-allowed;
  }
`;

const Container = styled.header`
  > section {
    display: flex;
    align-items: center;
    height: 56px;
    padding: 0 14px;
    border-bottom: solid 1px #f0f2f5;
    background: #fff;
  }
  > div {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 56px;
    padding: 0 14px;
    background: #fff;
    > section {
      display: flex;
      align-items: center;
      > * {
        margin-right: 14px;
      }
    }
  }
`;

export default Filter;
